import { LegalLayout, Section, LEGAL } from "@/components/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      testid="privacy-page"
      title="Privacy Policy"
      path="/privacy"
      seoTitle="Privacy Policy | Food Recall Alerts Australia"
      seoDescription="How Food Recall Alerts handles your mobile number and allergen preferences under the Privacy Act 1988 and the Australian Privacy Principles. We never sell your data."
      intro="We collect the least we can get away with: a mobile number, a state, and any allergens you want prioritised. We never sell it. This policy explains exactly how it is handled."
    >
      <Section n={1} title="Our commitment">
        <p>
          We handle personal information in accordance with the Privacy Act 1988 (Cth) and the
          Australian Privacy Principles (APPs). We do not sell, rent or trade your personal information,
          and we do not use it for advertising.
        </p>
      </Section>

      <Section n={2} title="What we collect">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Your Australian mobile number (required to send alerts and to log you in).</li>
          <li>Your state or territory and coverage preference.</li>
          <li>The allergens and recall types you ask us to prioritise.</li>
          <li>
            Service records: verification codes, when messages were sent, delivery status returned by
            the carrier, message length and cost, and the time of your last login.
          </li>
          <li>
            Billing records held by Stripe (a customer and subscription identifier, plan and payment
            status). <strong>We never see or store your card details.</strong>
          </li>
          <li>Basic technical data such as IP address used for rate limiting and abuse prevention.</li>
        </ul>
        <p>
          Allergen selections may be health-related information, which is sensitive information under
          the Privacy Act. We collect it only with your consent and use it only to decide which recall
          alerts to send you and how to word them.
        </p>
      </Section>

      <Section n={3} title="Why we collect it">
        <p>
          To verify your number, to match published FSANZ recalls to your household and send you the
          relevant SMS, to run your subscription and referral credits, to keep the service reliable and
          secure, and to meet our legal obligations.
        </p>
      </Section>

      <Section n={4} title="Who we share it with">
        <p>We use a small number of processors, and only for the purpose of running the service:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li><strong>Twilio</strong> - delivers the SMS. Receives your mobile number and message text.</li>
          <li><strong>Stripe</strong> - processes payments and stores card data under PCI DSS.</li>
          <li><strong>Anthropic</strong> - wording of an alert may be drafted by a language model. It receives the public FSANZ recall details and your allergen keywords, never your mobile number.</li>
          <li>Cloud hosting and database providers that store the data described above.</li>
        </ul>
        <p>
          Some of these providers process data outside Australia, including in the United States. We
          disclose information overseas only to these processors and only for the purposes above. We may
          also disclose information where required by law.
        </p>
      </Section>

      <Section n={5} title="Security and retention">
        <p>
          Data is transmitted over encrypted connections and access is restricted to those who need it.
          Verification codes are short-lived. We keep your account data while your account is open and
          retain limited SMS delivery and billing records afterwards where we must for accounting or
          compliance, with the phone number removed.
        </p>
      </Section>

      <Section n={6} title="Access, correction and deletion">
        <p>
          You can view and change your state, coverage, allergens and SMS setting at any time in your
          settings, and delete your account there. You may also ask us for a copy of the personal
          information we hold about you, or ask us to correct or delete it, by emailing {LEGAL.email}.
          We will respond within 30 days.
        </p>
      </Section>

      <Section n={7} title="Marketing and unsubscribing">
        <p>
          Our messages are service messages: recall alerts, verification codes, and account or
          subscription notices. We do not put advertising in alerts. Reply STOP to any message to stop
          all SMS immediately, or turn SMS off in your settings.
        </p>
      </Section>

      <Section n={8} title="Cookies">
        <p>
          We use browser local storage to keep you signed in and to remember display preferences such as
          your feed layout and a referral code you arrived with. We do not run third-party advertising or
          tracking cookies.
        </p>
      </Section>

      <Section n={9} title="Children">
        <p>
          The service is intended for people aged 16 and over. If you believe a child has signed up,
          contact us and we will delete the account.
        </p>
      </Section>

      <Section n={10} title="Complaints">
        <p>
          If you think we have mishandled your personal information, email {LEGAL.email} and we will
          investigate and respond. If you are not satisfied with our response you may complain to the
          Office of the Australian Information Commissioner at oaic.gov.au.
        </p>
      </Section>
    </LegalLayout>
  );
}
